import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Icon, InputText } from '@/components/shared';
import { useOnClickAway } from '@/helpers';
import { SelectItem } from '@/types';
import { AutoCompleteOption } from './AutoCompleteOption';
import styles from './autocomplete.module.scss';

interface AutoCompleteProps {
	onChange: (e) => void;
	onSelect: (e: SelectItem) => void;
	options: SelectItem[];
	name: string;
	id: string;
	label: string;
	value: string;
	disabled?: boolean;
	variant?: 'default' | 'inverse';
	forceClose?: boolean;
	forceClear?: boolean;
}

export const AutoComplete: React.FunctionComponent<AutoCompleteProps> = ({
	onChange,
	onSelect,
	options,
	name,
	id,
	label,
	disabled,
	variant,
	value,
	forceClose,
	forceClear,
}) => {
	const [innerValue, setInnerValue] = useState(value);
	const wrapperRef = React.useRef<HTMLDivElement>(null);
	const [isOpen, setOpen] = useState(options.length > 0);

	const handleClose = useCallback((): void => {
		setOpen(false);
	}, []);
	useOnClickAway(wrapperRef, handleClose, isOpen);

	const modifyValue = (e) => {
		setInnerValue(e.target.value);
		onChange(e);
	};

	const handleClick = () => {
		if (options.length > 0) {
			setOpen(true);
		}
	};

	const handleSelectAndClose = (option: SelectItem) => {
		if (option.value?.[0] !== null) {
			setInnerValue(option.value?.[0] as string);
			onSelect(option);
			handleClose();
		}
	};

	useEffect(() => {
		setOpen(options.length > 0);
	}, [options]);

	useEffect(() => {
		if (forceClose) {
			handleClose();
		}
	}, [forceClose, handleClose]);

	useEffect(() => {
		if (forceClear) {
			setInnerValue('');
		}
	}, [forceClear]);

	return (
		<div className={classNames(styles.wrapper, { [styles.isOpen]: isOpen })}>
			<InputText
				name={name}
				id={id}
				onChange={modifyValue}
				onClick={handleClick}
				label={label}
				disabled={disabled}
				variant={variant}
				value={innerValue}
			/>
			<Icon name={'search'} size={'sm'} className={styles.icon} />
			{isOpen && (
				<div className={styles.dropdownContent} ref={wrapperRef}>
					{options.map((option) => (
						<div key={option.label}>
							<AutoCompleteOption option={option} onSelect={handleSelectAndClose} />
						</div>
					))}
				</div>
			)}
		</div>
	);
};
